import React, { useState } from 'react';
import { Environment } from 'fym-common/src/config/types';
import { buildImgproxyUrl } from 'fym-shared/src/images/buildImgproxyUrl';
import { TypedMemo } from '../../../common/types';
import { config } from '../../../../config/config';
import { SMLInitialsAvatar } from './SMLInitialsAvatar';

export interface SMLImageProps {
  /** Null or missing when the entity has no image. The initials of `alt` are drawn instead. */
  src?: string | null;
  alt: string;
  /** Display width in CSS pixels. The actual rendered image is 3× for retina. */
  width?: number;
  /** Quality 1-100. Defaults to 85. */
  quality?: number;
  /** Force JPEG output. Otherwise imgproxy negotiates on the Accept header. */
  forceJPEG?: boolean;
  /** Crop to square using width as both width and height (smart gravity). */
  makeSquareFit?: boolean;
  className?: string;
  style?: React.CSSProperties;
  loading?: 'lazy' | 'eager';
  decoding?: 'async' | 'auto' | 'sync';
  crossOrigin?: 'anonymous' | 'use-credentials' | '';
  referrerPolicy?:
    | 'no-referrer'
    | 'no-referrer-when-downgrade'
    | 'origin'
    | 'origin-when-cross-origin'
    | 'same-origin'
    | 'strict-origin'
    | 'strict-origin-when-cross-origin'
    | 'unsafe-url';
  sizes?: string;
  srcSet?: string;
  useMap?: string;
  onLoad?: React.ReactEventHandler<HTMLImageElement>;
  onError?: React.ReactEventHandler<HTMLImageElement>;
}

/**
 * Resizes a storage-backed image via imgproxy. Returns the original URL
 * unchanged when the input is not from our S3 origin.
 */
export const transformImageUrl = (
  imageUrl: string | undefined,
  width?: number,
  quality?: number,
  forceJPEG?: boolean,
  makeSquareFit?: boolean
): string | undefined => {
  if (!imageUrl) {
    return undefined;
  }

  const { imageStorageBaseURL, imageProcessingURL, environment } = config;

  if (!imageUrl.startsWith(imageStorageBaseURL)) {
    return imageUrl;
  }

  const finalWidth = Math.min(1920, (width ?? 1920) * 3);

  // In local dev imgproxy fetches from the in-docker S3 mock, so the
  // browser-facing localhost URL is rewritten to the in-network hostname.
  let sourceURL = imageUrl;
  if (environment === Environment.local) {
    sourceURL = sourceURL.replace('http://localhost:9091', 'http://s3:9090');
  }

  return buildImgproxyUrl(imageProcessingURL, sourceURL, {
    width: finalWidth,
    quality,
    forceJPEG,
    makeSquareFit,
  });
};

const SMLImageComponent: React.FC<SMLImageProps> = ({
  src,
  alt,
  width,
  quality,
  forceJPEG,
  makeSquareFit,
  className,
  style,
  loading,
  decoding,
  crossOrigin,
  referrerPolicy,
  sizes,
  srcSet,
  useMap,
  onLoad,
  onError,
}) => {
  const transformedSrc = transformImageUrl(src ?? undefined, width, quality, forceJPEG, makeSquareFit);
  // Remembers the URL that failed, so a new src gets a fresh try.
  const [failedSrc, setFailedSrc] = useState<string | undefined>(undefined);

  if (transformedSrc === undefined || transformedSrc === failedSrc) {
    return <SMLInitialsAvatar name={alt} width={width} className={className} style={style} />;
  }

  return (
    <img
      src={transformedSrc}
      alt={alt}
      className={`sml-image ${className ?? ''}`}
      style={style}
      loading={loading}
      decoding={decoding}
      crossOrigin={crossOrigin}
      referrerPolicy={referrerPolicy}
      sizes={sizes}
      srcSet={srcSet}
      useMap={useMap}
      onLoad={onLoad}
      onError={(event) => {
        setFailedSrc(transformedSrc);
        onError?.(event);
      }}
    />
  );
};

export const SMLImage = TypedMemo(SMLImageComponent);
